import { cn } from "@/lib/utils";
import { ChevronDown, CircleCheck } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select/select";
import * as SelectPrimitive from "@radix-ui/react-select";
import React, { useState } from "react";
import { Skeleton } from "../skeleton/skeleton";
import { useTranslation } from "react-i18next";

export type ChipOption = {
  value: string;
  label: string;
  completed?: boolean;
};

export type ChipProps = {
  title?: string;
  value: string;
  icon: React.ReactNode;
  loading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  expandable?: boolean;
  options?: ChipOption[];
  onSelect?: (value: string) => void;
};

export function Chip(props: ChipProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const label = props.options?.find(
    (item) => item.value === props.value
  )?.label;

  const handleRetry = () => props.error && props.onRetry?.();

  return (
    <Select
      value={props.value}
      onValueChange={props.onSelect}
      onOpenChange={setOpen}
      disabled={!props.expandable || props.loading || !!props.error}
    >
      <SelectTrigger
        name={props.value}
        className={cn(
          "bg-header-chip w-fit text-white p-2 text-nowrap text-start outline-none",
          props.error && "bg-destructive cursor-pointer"
        )}
        onPointerDown={handleRetry}
      >
        {props.icon}
        <div>
          <p className="text-2xs font-bold">{props.title}</p>
          {props.loading && (
            <Skeleton className="h-6 w-full bg-primary-foreground/50" />
          )}

          {props.error ? (
            <p>{t("common.errors.retry")}</p>
          ) : (
            <SelectValue>
              <div className="flex gap-1 items-center text-sm leading-6">
                {props.options ? (
                  <>
                    <p>{label}</p>
                    <Counter number={props.options?.length - 1} />
                  </>
                ) : (
                  props.value
                )}
              </div>
            </SelectValue>
          )}
        </div>

        {props.expandable && props.options && (
          <SelectPrimitive.Icon asChild>
            <ChevronDown
              className={cn(
                "transition-transform duration-200",
                open && "-rotate-180"
              )}
            />
          </SelectPrimitive.Icon>
        )}
      </SelectTrigger>
      {props.expandable && props.options && (
        <SelectContent>
          <div className="flex flex-col gap-2 p-2">
            {props.options.map((item, i) => (
              <SelectItem
                key={i}
                className={cn(
                  "border border-primary cursor-pointer px-2",
                  item.value === props.value ? "bg-primary text-white" : "",
                  item.completed && "border-success"
                )}
                value={item.value}
              >
                {item.label}
                {item.completed && (
                  <CircleCheck className="ml-auto h-4 w-4 text-success" />
                )}
              </SelectItem>
            ))}
          </div>
        </SelectContent>
      )}
    </Select>
  );
}

function Counter({ number }: { number: number }) {
  if (number <= 0) return;
  return (
    <div className="bg-white text-blue-800 rounded-full p-1 flex items-center justify-center w-6 h-6 text-xs ">
      <p className="text-sm ">+</p>
      {number}
    </div>
  );
}
